import React, { useEffect, useMemo, useState } from "react";

/**
 * TonelagGroupsExplorer
 *
 * Research-oriented interface for checking Martin Kloster Jensen's
 * "Bokmålets tonelagspar («Vippere»)" against the printed book.
 *
 * Required data:
 *   data.classification.ending_classes
 *   data.classification.groups
 *   data.pair_candidates
 *
 * Optional props:
 *   manualCorrections     Object keyed by record ID
 *   scanImageBasePath     Example: "/book_tonelag"
 *                         Resolves viewer page 17 as
 *                         /book_tonelag/page_0017.jpg
 *   getScanImageUrl       Custom function: pair => string | null
 *   getSourcePageUrl      Custom function: pair => string | null
 *   onReviewChange        Called after verify/correct/flag/reject actions
 *   initialMode           "browse" | "review"
 *
 * No third-party UI package is required. Tailwind CSS is expected.
 */

const DEFAULT_MANUAL_CORRECTIONS = {
  "g01-0014": {
    accent_2_form: "dingle",
    verified_against_scan: true,
    note: "Corrected from OCR «diugle» after checking printed page 15.",
    frequency_is_stale: true,
  },
};

const QUALITY_STYLES = {
  green: {
    label: "High confidence",
    shortLabel: "High",
    dot: "bg-emerald-500",
    border: "border-l-emerald-500",
    row: "hover:bg-emerald-50/60",
    badge: "border-emerald-200 bg-emerald-50 text-emerald-800",
    bar: "bg-emerald-500",
  },
  yellow: {
    label: "Needs review",
    shortLabel: "Review",
    dot: "bg-amber-400",
    border: "border-l-amber-400",
    row: "bg-amber-50/35 hover:bg-amber-50",
    badge: "border-amber-200 bg-amber-50 text-amber-900",
    bar: "bg-amber-400",
  },
  red: {
    label: "Low confidence",
    shortLabel: "Low",
    dot: "bg-rose-500",
    border: "border-l-rose-500",
    row: "bg-rose-50/40 hover:bg-rose-50",
    badge: "border-rose-200 bg-rose-50 text-rose-800",
    bar: "bg-rose-500",
  },
};

const ENDING_CLASS_LABELS = {
  final_schwa: "Utlyd -ə",
  final_er: "Utlyd -er",
  final_en: "Utlyd -en",
  other_endings: "Andre utlydsformer",
};

const FREQUENCY_TIER_LABELS = {
  very_common: "Very common",
  common: "Common",
  moderate: "Moderate",
  uncommon: "Uncommon",
  rare: "Rare",
  very_rare: "Very rare",
  unattested: "Unattested",
  not_measurable: "Not measurable",
};

const DISAMBIGUATION_LABELS = {
  different_spelling_but_lexical_disambiguation_may_still_be_needed:
    "Different spellings; POS or lexical verification may still be needed.",
  identical_spelling_pos_or_morphology_may_separate:
    "Identical spelling; POS or morphology may separate the members.",
  identical_spelling_semantic_disambiguation_required:
    "Identical spelling; semantic disambiguation is required.",
  surface_form_not_cleanly_measurable:
    "At least one OCR form cannot be treated as a clean written token.",
};

const REVIEW_STATUS_LABELS = {
  verified: "Verified against scan",
  corrected: "Corrected; verify once more",
  needs_correction: "Flagged for correction",
  rejected: "Rejected",
};

const nf = new Intl.NumberFormat("nb-NO");
const decimal = new Intl.NumberFormat("nb-NO", {
  maximumFractionDigits: 3,
});

function clamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function normalize(value) {
  return String(value ?? "")
    .normalize("NFC")
    .toLocaleLowerCase("nb-NO");
}

function padPage(value) {
  return String(value ?? "").padStart(4, "0");
}

function morphologyText(alternatives) {
  if (!Array.isArray(alternatives) || alternatives.length === 0) return "—";
  return alternatives.map((tags) => tags.join(" · ")).join("  /  ");
}

function getPairFrequencyMetric(pair) {
  const evidence = pair?.frequency_evidence?.pair;
  if (!evidence) return null;

  if (evidence.written_forms_identical_casefold) {
    const ppm = evidence.shared_surface_frequency_per_million;
    return Number.isFinite(ppm)
      ? {
          ppm,
          basis: "shared_written_form",
          count: evidence.shared_surface_token_count,
          tier: evidence.shared_surface_frequency_tier,
        }
      : null;
  }

  const ppm = evidence.rarer_written_side_frequency_per_million;
  return Number.isFinite(ppm)
    ? {
        ppm,
        basis: "rarer_written_side",
        count: evidence.rarer_written_side_count,
        tier: evidence.pedagogical_surface_frequency_tier,
      }
    : null;
}

function applyPatches(pair, manualCorrections, reviewOverrides) {
  const manual = manualCorrections?.[pair.id];
  const review = reviewOverrides?.[pair.id];

  return {
    ...pair,
    accent_1: {
      ...pair.accent_1,
      form_display:
        review?.accent_1_form ??
        manual?.accent_1_form ??
        pair.accent_1?.form_ocr,
    },
    accent_2: {
      ...pair.accent_2,
      form_display:
        review?.accent_2_form ??
        manual?.accent_2_form ??
        pair.accent_2?.form_ocr,
    },
    manual_correction: manual ?? null,
    research_review: review ?? null,
  };
}

function getDisplayForms(pair) {
  return {
    accent1: pair?.accent_1?.form_display ?? pair?.accent_1?.form_ocr ?? "—",
    accent2: pair?.accent_2?.form_display ?? pair?.accent_2?.form_ocr ?? "—",
  };
}

/**
 * "Text confidence" is a transparent review heuristic, not a calibrated
 * character-recognition probability.
 */
function getDisplayQuality(pair) {
  const review = pair?.research_review;
  const manual = pair?.manual_correction;

  if (review?.status === "verified" || manual?.verified_against_scan) {
    return {
      score: 1,
      level: "green",
      reasons: ["Verified manually against the printed scan."],
      source: "manual",
    };
  }

  if (review?.status === "rejected") {
    return {
      score: 0,
      level: "red",
      reasons: ["Record was rejected during research review."],
      source: "manual",
    };
  }

  let score = Number(pair?.structural_parse_assessment?.score ?? 0.5);
  const reasons = [];

  const parseFlags = pair?.structural_parse_assessment?.flags ?? [];
  if (parseFlags.length > 0) {
    score -= Math.min(0.28, parseFlags.length * 0.07);
    reasons.push(...parseFlags.map((flag) => `Parser flag: ${flag}`));
  }

  const a = pair?.frequency_evidence?.accent_1_written_form;
  const b = pair?.frequency_evidence?.accent_2_written_form;
  const aMeasurable = a?.measurable === true;
  const bMeasurable = b?.measurable === true;

  if (!aMeasurable || !bMeasurable) {
    score -= 0.3;
    reasons.push("At least one form is not a clean measurable token.");
  } else {
    const aZero = a?.token_count_casefold === 0;
    const bZero = b?.token_count_casefold === 0;

    if (aZero && bZero) {
      score -= 0.3;
      reasons.push("Neither OCR form occurs in the frequency corpus.");
    } else if (aZero || bZero) {
      score -= 0.14;
      reasons.push("One OCR form has zero corpus matches.");
    }
  }

  if (review?.status === "needs_correction") {
    score = Math.min(score, 0.65);
    reasons.unshift("Flagged for correction during research review.");
  }

  if (review?.status === "corrected") {
    score = Math.min(Math.max(score, 0.58), 0.76);
    reasons.unshift("A correction was entered but has not been verified.");
  }

  if (pair?.lexical_verification?.verified_against_scan !== true) {
    score -= 0.03;
  }

  score = clamp(score);
  const level = score >= 0.8 ? "green" : score >= 0.55 ? "yellow" : "red";

  if (reasons.length === 0) {
    reasons.push("No major structural or corpus anomaly was detected.");
  }

  return { score, level, reasons, source: "heuristic" };
}

function getCorpusStatus(pair) {
  if (
    pair?.manual_correction?.frequency_is_stale ||
    pair?.research_review?.frequency_is_stale
  ) {
    return {
      level: "pending",
      short: "Frequency pending",
      long: "The displayed form changed after corpus lookup. Frequency must be recalculated.",
    };
  }

  const a = pair?.frequency_evidence?.accent_1_written_form;
  const b = pair?.frequency_evidence?.accent_2_written_form;
  const metric = getPairFrequencyMetric(pair);

  if (!a?.measurable || !b?.measurable) {
    return {
      level: "warning",
      short: "Frequency unavailable",
      long: "At least one side is not a clean single-token form.",
    };
  }

  const aZero = a.token_count_casefold === 0;
  const bZero = b.token_count_casefold === 0;

  if (aZero && bZero) {
    return {
      level: "warning",
      short: "0 matches — check OCR",
      long: "Neither written form was found. This may indicate OCR error, historical spelling, or corpus absence.",
    };
  }

  if (aZero || bZero) {
    return {
      level: "warning",
      short: "One side has 0 matches",
      long: "One written form was not found. Check the scan before interpreting this as rarity.",
    };
  }

  if (!metric) {
    return {
      level: "neutral",
      short: "No frequency",
      long: "No pair-level frequency metric is available.",
    };
  }

  return {
    level: "measured",
    short: FREQUENCY_TIER_LABELS[metric.tier] ?? metric.tier ?? "Measured",
    long:
      metric.basis === "shared_written_form"
        ? "Shared written-form frequency; the two tonal meanings remain unresolved."
        : "Tier based on the rarer written side.",
  };
}

function buildRanks(pairs) {
  const measurable = pairs
    .map((pair) => ({ pair, metric: getPairFrequencyMetric(pair) }))
    .filter(
      ({ metric, pair }) =>
        metric &&
        !pair?.manual_correction?.frequency_is_stale &&
        !pair?.research_review?.frequency_is_stale &&
        pair?.research_review?.status !== "rejected"
    )
    .sort((a, b) => b.metric.ppm - a.metric.ppm);

  const global = new Map();
  measurable.forEach(({ pair, metric }, index) => {
    global.set(pair.id, {
      rank: index + 1,
      total: measurable.length,
      percentile:
        measurable.length > 1
          ? 100 * (1 - index / (measurable.length - 1))
          : 100,
      metric,
    });
  });

  const byGroup = new Map();
  const buckets = new Map();

  measurable.forEach((entry) => {
    const groupId = entry.pair.group_id;
    if (!buckets.has(groupId)) buckets.set(groupId, []);
    buckets.get(groupId).push(entry);
  });

  buckets.forEach((entries, groupId) => {
    const groupMap = new Map();
    entries.forEach(({ pair, metric }, index) => {
      groupMap.set(pair.id, {
        rank: index + 1,
        total: entries.length,
        metric,
      });
    });
    byGroup.set(groupId, groupMap);
  });

  return { global, byGroup };
}

function SearchIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function ArrowIcon({ direction }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={`h-4 w-4 ${direction === "right" ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M14 5h5v5M19 5l-9 9" />
      <path d="M19 13v6H5V5h6" />
    </svg>
  );
}

function QualityLegend() {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
      {Object.entries(QUALITY_STYLES).map(([key, style]) => (
        <span key={key} className="inline-flex items-center gap-1.5">
          <span className={`h-2.5 w-2.5 rounded-full ${style.dot}`} />
          {style.label}
        </span>
      ))}
      <span className="text-slate-400">
        Text confidence combines structural parsing, corpus anomalies and manual review.
      </span>
    </div>
  );
}

function CorpusBadge({ pair }) {
  const status = getCorpusStatus(pair);

  const styles = {
    measured: "border-indigo-200 bg-indigo-50 text-indigo-800",
    warning: "border-amber-200 bg-amber-50 text-amber-900",
    pending: "border-sky-200 bg-sky-50 text-sky-800",
    neutral: "border-slate-200 bg-slate-50 text-slate-600",
  };

  return (
    <span
      title={status.long}
      className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${styles[status.level]}`}
    >
      {status.short}
    </span>
  );
}

function VerificationBadge({ pair }) {
  const status = pair?.research_review?.status;
  const manualVerified = pair?.manual_correction?.verified_against_scan;

  if (manualVerified || status === "verified") {
    return (
      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
        Scan verified
      </span>
    );
  }

  if (status === "corrected") {
    return (
      <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-800">
        Correction entered
      </span>
    );
  }

  if (status === "needs_correction") {
    return (
      <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
        Flagged
      </span>
    );
  }

  if (status === "rejected") {
    return (
      <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-800">
        Rejected
      </span>
    );
  }

  return null;
}

function PairRow({ pair, selected, onSelect }) {
  const forms = getDisplayForms(pair);
  const quality = getDisplayQuality(pair);
  const style = QUALITY_STYLES[quality.level];

  return (
    <button
      type="button"
      onClick={() => onSelect(pair)}
      aria-pressed={selected}
      className={[
        "group mb-0.5 w-full break-inside-avoid border-b border-l-4 border-b-stone-200",
        "px-3 py-2.5 text-left transition focus:outline-none focus:ring-2",
        "focus:ring-slate-500 focus:ring-inset",
        style.border,
        style.row,
        selected ? "bg-slate-100 ring-2 ring-slate-700 ring-inset" : "",
      ].join(" ")}
    >
      <div className="flex items-start gap-2.5">
        <span
          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${style.dot}`}
          title={`${style.label}: ${Math.round(quality.score * 100)}%`}
        />

        <div className="min-w-0 flex-1">
          <div className="font-serif text-[15px] leading-5 text-slate-950">
            <span className="font-medium">{forms.accent1}</span>
            <span className="px-1.5 text-stone-400">/</span>
            <span>{forms.accent2}</span>
          </div>

          <div className="mt-1 flex min-h-5 flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-medium uppercase tracking-wide text-stone-500">
              Text {Math.round(quality.score * 100)}%
            </span>
            <VerificationBadge pair={pair} />
            <CorpusBadge pair={pair} />
          </div>
        </div>

        <span className="mt-1 text-xs text-stone-300 opacity-0 transition group-hover:opacity-100">
          Open
        </span>
      </div>
    </button>
  );
}

function Stat({ label, value, helper }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-slate-950">{value}</div>
      {helper ? <div className="mt-1 text-xs leading-5 text-slate-500">{helper}</div> : null}
    </div>
  );
}

function FrequencySide({ title, evidence }) {
  if (!evidence?.measurable) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="text-xs font-semibold text-slate-700">{title}</div>
        <div className="mt-1 text-sm text-slate-500">Not measurable</div>
        {evidence?.reason ? (
          <div className="mt-1 text-xs text-slate-400">{evidence.reason}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xs font-semibold text-slate-700">{title}</div>
      <div className="mt-1 font-serif text-lg text-slate-950">
        {evidence.surface_form}
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <dt className="text-slate-500">Tokens</dt>
        <dd className="text-right font-medium text-slate-800">
          {nf.format(evidence.token_count_casefold ?? 0)}
        </dd>
        <dt className="text-slate-500">Per million</dt>
        <dd className="text-right font-medium text-slate-800">
          {decimal.format(evidence.frequency_per_million ?? 0)}
        </dd>
        <dt className="text-slate-500">Tier</dt>
        <dd className="text-right font-medium text-slate-800">
          {FREQUENCY_TIER_LABELS[evidence.frequency_tier] ??
            evidence.frequency_tier ??
            "—"}
        </dd>
      </dl>
    </div>
  );
}

function SourceComparison({
  pair,
  scanImageUrl,
  sourcePageUrl,
}) {
  const forms = getDisplayForms(pair);
  const raw = pair?.source?.raw_ocr;
  const differsFromRaw =
    normalize(raw).replace(/\s+/g, " ") !==
    normalize(`${forms.accent1} / ${forms.accent2}`).replace(/\s+/g, " ");

  return (
    <section className="rounded-xl border border-stone-300 bg-[#fbfaf6]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-300 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-950">Compare with the book</h3>
          <p className="mt-0.5 text-xs text-stone-500">
            Viewer page {pair?.source?.viewer_page ?? "—"} · printed page{" "}
            {pair?.source?.printed_page_estimate ?? "—"}
          </p>
        </div>

        {sourcePageUrl ? (
          <a
            href={sourcePageUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-400 hover:bg-stone-50"
          >
            Open source page
            <ExternalIcon />
          </a>
        ) : null}
      </div>

      {scanImageUrl ? (
        <div className="border-b border-stone-300 bg-stone-100 p-3">
          <div className="max-h-[420px] overflow-auto rounded border border-stone-300 bg-white">
            <img
              src={scanImageUrl}
              alt={`Scan of viewer page ${pair?.source?.viewer_page ?? ""}`}
              className="mx-auto max-w-none"
              loading="lazy"
            />
          </div>
        </div>
      ) : (
        <div className="border-b border-stone-300 bg-stone-50 px-4 py-3 text-xs text-stone-500">
          Add <code className="rounded bg-white px-1">scanImageBasePath</code> or{" "}
          <code className="rounded bg-white px-1">getScanImageUrl</code> to show
          the scanned page here.
        </div>
      )}

      <div className="grid gap-3 p-4 sm:grid-cols-2">
        <div className="rounded-lg border border-stone-200 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            Raw OCR
          </div>
          <div className="mt-1 font-serif text-base text-slate-900">{raw ?? "—"}</div>
        </div>

        <div
          className={[
            "rounded-lg border p-3",
            differsFromRaw
              ? "border-sky-200 bg-sky-50"
              : "border-stone-200 bg-white",
          ].join(" ")}
        >
          <div className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            Current database form
          </div>
          <div className="mt-1 font-serif text-base text-slate-950">
            {forms.accent1} / {forms.accent2}
          </div>
          {differsFromRaw ? (
            <div className="mt-1 text-xs text-sky-800">Edited from the OCR form.</div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function ReviewActions({
  pair,
  onApplyReview,
}) {
  const forms = getDisplayForms(pair);
  const [editing, setEditing] = useState(false);
  const [accent1, setAccent1] = useState(forms.accent1);
  const [accent2, setAccent2] = useState(forms.accent2);
  const [note, setNote] = useState(pair?.research_review?.note ?? "");

  useEffect(() => {
    setEditing(false);
    setAccent1(forms.accent1);
    setAccent2(forms.accent2);
    setNote(pair?.research_review?.note ?? "");
  }, [pair.id, forms.accent1, forms.accent2, pair?.research_review?.note]);

  function commit(status, additional = {}) {
    onApplyReview(pair.id, {
      ...pair?.research_review,
      status,
      note,
      reviewed_at: new Date().toISOString(),
      ...additional,
    });
  }

  return (
    <section className="rounded-xl border border-slate-300 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-950">Research decision</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Decisions are stored locally unless an <code>onReviewChange</code> callback persists them.
          </p>
        </div>

        {pair?.research_review?.status ? (
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
            {REVIEW_STATUS_LABELS[pair.research_review.status] ??
              pair.research_review.status}
          </span>
        ) : null}
      </div>

      {editing ? (
        <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-semibold text-slate-700">
              Accent 1 form
              <input
                value={accent1}
                onChange={(event) => setAccent1(event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-serif text-base font-normal outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
              />
            </label>
            <label className="text-xs font-semibold text-slate-700">
              Accent 2 form
              <input
                value={accent2}
                onChange={(event) => setAccent2(event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-serif text-base font-normal outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
              />
            </label>
          </div>

          <label className="mt-3 block text-xs font-semibold text-slate-700">
            Review note
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-normal outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
              placeholder="Describe what was corrected and why."
            />
          </label>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                commit("corrected", {
                  accent_1_form: accent1.trim(),
                  accent_2_form: accent2.trim(),
                  frequency_is_stale:
                    accent1.trim() !== pair?.accent_1?.form_ocr ||
                    accent2.trim() !== pair?.accent_2?.form_ocr,
                })
              }
              className="rounded-md bg-sky-700 px-3 py-2 text-xs font-semibold text-white hover:bg-sky-800"
            >
              Save correction
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <label className="mt-4 block text-xs font-semibold text-slate-700">
            Review note
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-normal outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              placeholder="Optional note about the scan, spelling or grouping."
            />
          </label>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => commit("verified")}
              className="rounded-md bg-emerald-700 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-800"
            >
              Mark verified
            </button>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-900 hover:bg-sky-100"
            >
              Correct spelling
            </button>
            <button
              type="button"
              onClick={() => commit("needs_correction")}
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-950 hover:bg-amber-100"
            >
              Flag for correction
            </button>
            <button
              type="button"
              onClick={() => commit("rejected")}
              className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-900 hover:bg-rose-100"
            >
              Reject record
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function PairDetailsDrawer({
  pair,
  group,
  globalRank,
  groupRank,
  scanImageUrl,
  sourcePageUrl,
  previousPair,
  nextPair,
  nextReviewPair,
  onNavigate,
  onClose,
  onApplyReview,
}) {
  useEffect(() => {
    if (!pair) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft" && previousPair) onNavigate(previousPair);
      if (event.key === "ArrowRight" && nextPair) onNavigate(nextPair);
      if (event.key.toLocaleLowerCase() === "n" && nextReviewPair) {
        onNavigate(nextReviewPair);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    pair,
    previousPair,
    nextPair,
    nextReviewPair,
    onClose,
    onNavigate,
  ]);

  if (!pair) return null;

  const forms = getDisplayForms(pair);
  const quality = getDisplayQuality(pair);
  const qualityStyle = QUALITY_STYLES[quality.level];
  const evidence = pair?.frequency_evidence;
  const pairEvidence = evidence?.pair;
  const corpusStatus = getCorpusStatus(pair);
  const disambiguation =
    DISAMBIGUATION_LABELS[pairEvidence?.disambiguation_class] ??
    pairEvidence?.disambiguation_class ??
    "No disambiguation information.";
  const frequencyMetric = getPairFrequencyMetric(pair);
  const structuralScore = pair?.structural_parse_assessment?.score;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close pair details"
        className="absolute inset-0 bg-slate-950/30 backdrop-blur-[1px]"
        onClick={onClose}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="pair-details-title"
        className="relative h-full w-full max-w-3xl overflow-y-auto border-l border-slate-300 bg-slate-50 shadow-2xl"
      >
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="flex items-center justify-between gap-3 px-5 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                disabled={!previousPair}
                onClick={() => previousPair && onNavigate(previousPair)}
                className="rounded-md border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-30"
                title="Previous visible pair (←)"
              >
                <ArrowIcon direction="left" />
              </button>
              <button
                type="button"
                disabled={!nextPair}
                onClick={() => nextPair && onNavigate(nextPair)}
                className="rounded-md border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-30"
                title="Next visible pair (→)"
              >
                <ArrowIcon direction="right" />
              </button>

              <div className="min-w-0 pl-1">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Group {pair.group_id} · record {pair.id}
                </div>
                <h2
                  id="pair-details-title"
                  className="truncate font-serif text-xl font-semibold text-slate-950"
                >
                  {forms.accent1} / {forms.accent2}
                </h2>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {nextReviewPair ? (
                <button
                  type="button"
                  onClick={() => onNavigate(nextReviewPair)}
                  className="hidden rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-950 hover:bg-amber-100 sm:inline-flex"
                  title="Next item requiring review (N)"
                >
                  Next review item
                </button>
              ) : null}

              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-950"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-5 p-5">
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Tonelag contrast
                </div>
                <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                  <div className="rounded-lg bg-slate-50 p-3 text-center">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Accent 1
                    </div>
                    <div className="mt-1 font-serif text-2xl text-slate-950">
                      {forms.accent1}
                    </div>
                  </div>
                  <div className="text-xl text-slate-300">↔</div>
                  <div className="rounded-lg bg-slate-50 p-3 text-center">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Accent 2
                    </div>
                    <div className="mt-1 font-serif text-2xl text-slate-950">
                      {forms.accent2}
                    </div>
                  </div>
                </div>
              </div>

              <span
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${qualityStyle.badge}`}
              >
                {qualityStyle.label}
              </span>
            </div>

            <div className="mt-4">
              <div className="flex justify-between text-xs text-slate-500">
                <span>Text confidence</span>
                <span>{Math.round(quality.score * 100)}%</span>
              </div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${qualityStyle.bar}`}
                  style={{ width: `${quality.score * 100}%` }}
                />
              </div>
              <ul className="mt-2 space-y-1 text-xs text-slate-500">
                {quality.reasons.map((reason) => (
                  <li key={reason}>• {reason}</li>
                ))}
              </ul>
              {Number.isFinite(structuralScore) ? (
                <p className="mt-2 text-[11px] leading-5 text-slate-400">
                  Structural parse score: {Math.round(structuralScore * 100)}%.
                  Text confidence is a review heuristic, not a calibrated OCR probability.
                </p>
              ) : null}
            </div>
          </section>

          <SourceComparison
            pair={pair}
            scanImageUrl={scanImageUrl}
            sourcePageUrl={sourcePageUrl}
          />

          <ReviewActions pair={pair} onApplyReview={onApplyReview} />

          <section>
            <h3 className="text-sm font-semibold text-slate-950">
              Corpus evidence
            </h3>

            <div
              className={[
                "mt-2 rounded-lg border p-3 text-sm",
                corpusStatus.level === "warning"
                  ? "border-amber-200 bg-amber-50 text-amber-950"
                  : corpusStatus.level === "pending"
                  ? "border-sky-200 bg-sky-50 text-sky-950"
                  : "border-indigo-100 bg-indigo-50 text-indigo-950",
              ].join(" ")}
            >
              <div className="font-semibold">{corpusStatus.short}</div>
              <p className="mt-1 text-xs leading-5">{corpusStatus.long}</p>
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2">
              <Stat
                label="Global surface rank"
                value={
                  corpusStatus.level === "pending"
                    ? "Refresh needed"
                    : globalRank
                    ? `#${globalRank.rank}`
                    : "—"
                }
                helper={
                  globalRank
                    ? `of ${nf.format(globalRank.total)} measurable records`
                    : "Not a meaning-specific tonelag rank"
                }
              />
              <Stat
                label="Rank in group"
                value={
                  corpusStatus.level === "pending"
                    ? "Refresh needed"
                    : groupRank
                    ? `#${groupRank.rank}`
                    : "—"
                }
                helper={
                  groupRank
                    ? `of ${nf.format(groupRank.total)} measurable records`
                    : `Group ${pair.group_id}`
                }
              />
              <Stat
                label="Pair metric"
                value={
                  corpusStatus.level === "pending"
                    ? "—"
                    : frequencyMetric
                    ? `${decimal.format(frequencyMetric.ppm)} ppm`
                    : "—"
                }
                helper={
                  frequencyMetric?.basis === "shared_written_form"
                    ? "Shared written-form frequency"
                    : frequencyMetric?.basis === "rarer_written_side"
                    ? "Rarer written side"
                    : "No pair-level metric"
                }
              />
              <Stat
                label="Surface tier"
                value={
                  corpusStatus.level === "pending"
                    ? "Pending"
                    : FREQUENCY_TIER_LABELS[frequencyMetric?.tier] ??
                      frequencyMetric?.tier ??
                      "—"
                }
                helper={
                  pairEvidence?.written_form_balance_ratio != null
                    ? `Balance ratio ${decimal.format(
                        pairEvidence.written_form_balance_ratio
                      )}`
                    : "Balance unavailable for shared spelling"
                }
              />
            </div>

            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <FrequencySide
                title="Accent 1 written form"
                evidence={evidence?.accent_1_written_form}
              />
              <FrequencySide
                title="Accent 2 written form"
                evidence={evidence?.accent_2_written_form}
              />
            </div>

            <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3 text-xs leading-5 text-slate-600">
              <div className="font-semibold text-slate-800">Interpretation</div>
              <p className="mt-1">{disambiguation}</p>
              {pairEvidence?.warning ? <p className="mt-1">{pairEvidence.warning}</p> : null}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-slate-950">
              Morphology and classification
            </h3>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-xs font-semibold text-slate-500">
                  Accent 1
                </div>
                <div className="mt-1 text-sm text-slate-800">
                  {morphologyText(
                    pair?.accent_1?.default_morphology_alternatives
                  )}
                </div>
                {pair?.accent_1?.inline_annotation_ocr ? (
                  <div className="mt-2 text-xs text-slate-500">
                    OCR annotation: {pair.accent_1.inline_annotation_ocr}
                  </div>
                ) : null}
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-xs font-semibold text-slate-500">
                  Accent 2
                </div>
                <div className="mt-1 text-sm text-slate-800">
                  {morphologyText(
                    pair?.accent_2?.default_morphology_alternatives
                  )}
                </div>
                {pair?.accent_2?.inline_annotation_ocr ? (
                  <div className="mt-2 text-xs text-slate-500">
                    OCR annotation: {pair.accent_2.inline_annotation_ocr}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">
                Group {pair.group_id}
              </div>
              <p className="mt-1">{group?.description ?? "No group description."}</p>
              <p className="mt-1 text-xs text-slate-500">
                Orthography relation: {pair?.orthography_relation ?? "—"} ·
                segmental equivalence claimed:{" "}
                {pair?.claimed_segmental_equivalence ? "yes" : "no"}
              </p>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-950">
              Record provenance
            </h3>
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-slate-500">Record ID</dt>
              <dd className="font-mono text-xs text-slate-800">{pair.id}</dd>
              <dt className="text-slate-500">Viewer page</dt>
              <dd className="text-slate-800">
                {pair?.source?.viewer_page ?? "—"}
              </dd>
              <dt className="text-slate-500">Printed page</dt>
              <dd className="text-slate-800">
                {pair?.source?.printed_page_estimate ?? "—"}
              </dd>
              <dt className="text-slate-500">OCR line</dt>
              <dd className="text-slate-800">
                {pair?.source?.ocr_line ?? "—"}
              </dd>
              <dt className="text-slate-500">Separator</dt>
              <dd className="font-mono text-slate-800">
                {pair?.source?.separator_ocr ?? "—"}
              </dd>
              <dt className="text-slate-500">Scan verified</dt>
              <dd className="text-slate-800">
                {pair?.manual_correction?.verified_against_scan ||
                pair?.research_review?.status === "verified" ||
                pair?.lexical_verification?.verified_against_scan
                  ? "Yes"
                  : "No"}
              </dd>
            </dl>
          </section>

          <p className="pb-3 text-center text-[11px] text-slate-400">
            Keyboard: ← previous · → next · N next review item · Esc close
          </p>
        </div>
      </aside>
    </div>
  );
}

function GroupNavButton({
  group,
  selected,
  onSelect,
  visibleCount,
  reviewCount,
}) {
  return (
    <button
      type="button"
      aria-current={selected ? "page" : undefined}
      onClick={() => onSelect(group.id)}
      style={
        selected
          ? {
              color: "#ffffff",
              backgroundColor: "#0f172a",
              borderColor: "#0f172a",
            }
          : undefined
      }
      className={[
        "relative flex w-full items-start justify-between gap-3 rounded-md border px-3 py-2 text-left",
        "transition outline-none focus-visible:ring-2 focus-visible:ring-slate-700 focus-visible:ring-offset-1",
        selected
          ? "!border-slate-900 !bg-slate-900 !text-white shadow-sm"
          : "border-transparent text-slate-700 hover:border-stone-300 hover:bg-stone-100",
      ].join(" ")}
    >
      {selected ? (
        <span
          aria-hidden="true"
          className="absolute inset-y-1 left-0 w-1 rounded-r bg-amber-400"
        />
      ) : null}

      <span
        className={[
          "min-w-0 pl-1",
          selected ? "!text-white" : "text-slate-700",
        ].join(" ")}
      >
        <span
          className={[
            "font-semibold",
            selected ? "!text-white" : "",
          ].join(" ")}
        >
          {group.id}.
        </span>{" "}
        <span
          className={[
            "font-serif",
            selected ? "!text-white" : "",
          ].join(" ")}
        >
          {group.example}
        </span>
      </span>

      <span className="flex shrink-0 items-center gap-1.5">
        {reviewCount > 0 ? (
          <span
            title={`${reviewCount} records require review`}
            className="h-2 w-2 rounded-full bg-amber-400"
          />
        ) : null}
        <span
          className={[
            "text-[11px] tabular-nums",
            selected ? "!text-slate-200" : "text-stone-500",
          ].join(" ")}
        >
          {visibleCount ?? group.extracted_pair_candidates ?? group.reported_count}
        </span>
      </span>
    </button>
  );
}

function FilterButton({ active, onClick, children, tone = "neutral" }) {
  const activeStyles = {
    neutral: "border-slate-900 bg-slate-900 text-white",
    green: "border-emerald-700 bg-emerald-700 text-white",
    yellow: "border-amber-500 bg-amber-400 text-amber-950",
    red: "border-rose-700 bg-rose-700 text-white",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full border px-3 py-1.5 text-xs font-semibold transition",
        active
          ? activeStyles[tone]
          : "border-stone-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-stone-50",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export default function TonelagGroupsExplorer({
  data,
  initialGroupId = 1,
  initialMode = "browse",
  manualCorrections = DEFAULT_MANUAL_CORRECTIONS,
  title = "Bokmålets tonelagspar",
  scanImageBasePath = null,
  getScanImageUrl = null,
  getSourcePageUrl = null,
  onReviewChange = null,
}) {
  const groups = data?.classification?.groups ?? [];
  const endingClasses = data?.classification?.ending_classes ?? [];
  const rawPairs = data?.pair_candidates ?? [];

  const [reviewOverrides, setReviewOverrides] = useState({});
  const [selectedGroupId, setSelectedGroupId] = useState(initialGroupId);
  const [selectedPairId, setSelectedPairId] = useState(null);
  const [query, setQuery] = useState("");
  const [qualityFilter, setQualityFilter] = useState("all");
  const [sortMode, setSortMode] = useState("book");
  const [viewMode, setViewMode] = useState(initialMode);
  const [mobileGroupsOpen, setMobileGroupsOpen] = useState(false);
  const [openEndingClasses, setOpenEndingClasses] = useState(() =>
    Object.fromEntries(endingClasses.map((endingClass) => [endingClass.id, true]))
  );

  const pairs = useMemo(
    () =>
      rawPairs.map((pair, bookIndex) => ({
        ...applyPatches(pair, manualCorrections, reviewOverrides),
        _bookIndex: bookIndex,
      })),
    [rawPairs, manualCorrections, reviewOverrides]
  );

  const groupsById = useMemo(
    () => new Map(groups.map((group) => [group.id, group])),
    [groups]
  );

  const pairsByGroup = useMemo(() => {
    const map = new Map();
    groups.forEach((group) => map.set(group.id, []));

    pairs.forEach((pair) => {
      if (!map.has(pair.group_id)) map.set(pair.group_id, []);
      map.get(pair.group_id).push(pair);
    });

    return map;
  }, [groups, pairs]);

  const ranks = useMemo(() => buildRanks(pairs), [pairs]);

  useEffect(() => {
    if (!groupsById.has(selectedGroupId) && groups.length > 0) {
      setSelectedGroupId(groups[0].id);
    }
  }, [groups, groupsById, selectedGroupId]);

  useEffect(() => {
    const selectedGroup = groupsById.get(selectedGroupId);
    if (!selectedGroup) return;

    setOpenEndingClasses((current) => ({
      ...current,
      [selectedGroup.ending_class]: true,
    }));
  }, [groupsById, selectedGroupId]);

  const selectedGroup = groupsById.get(selectedGroupId) ?? groups[0] ?? null;

  function isReviewItem(pair) {
    const level = getDisplayQuality(pair).level;
    return (
      level !== "green" ||
      pair?.research_review?.status === "needs_correction" ||
      pair?.research_review?.status === "corrected"
    );
  }

  function matchesPair(pair) {
    const quality = getDisplayQuality(pair);

    if (viewMode === "review" && !isReviewItem(pair)) return false;
    if (qualityFilter !== "all" && quality.level !== qualityFilter) return false;

    const normalizedQuery = normalize(query).trim();
    if (!normalizedQuery) return true;

    const forms = getDisplayForms(pair);
    const haystack = normalize(
      [
        forms.accent1,
        forms.accent2,
        pair.id,
        pair.group_id,
        pair?.source?.raw_ocr,
        pair?.source?.viewer_page,
        pair?.source?.printed_page_estimate,
        pair?.frequency_evidence?.pair?.pedagogical_surface_frequency_tier,
        pair?.research_review?.note,
      ].join(" ")
    );

    return haystack.includes(normalizedQuery);
  }

  const filteredAllPairs = useMemo(
    () => pairs.filter(matchesPair),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pairs, query, qualityFilter, viewMode]
  );

  const visiblePairs = useMemo(() => {
    const source = query.trim()
      ? filteredAllPairs
      : (pairsByGroup.get(selectedGroupId) ?? []).filter(matchesPair);

    return [...source].sort((a, b) => {
      if (viewMode === "review" || sortMode === "confidence") {
        return (
          getDisplayQuality(a).score - getDisplayQuality(b).score ||
          a._bookIndex - b._bookIndex
        );
      }

      if (sortMode === "frequency") {
        const af = getPairFrequencyMetric(a)?.ppm ?? -1;
        const bf = getPairFrequencyMetric(b)?.ppm ?? -1;
        return bf - af || a._bookIndex - b._bookIndex;
      }

      return a._bookIndex - b._bookIndex;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredAllPairs,
    pairsByGroup,
    selectedGroupId,
    query,
    qualityFilter,
    sortMode,
    viewMode,
  ]);

  const selectedPair =
    pairs.find((pair) => pair.id === selectedPairId) ?? null;

  const selectedGlobalRank = selectedPair
    ? ranks.global.get(selectedPair.id)
    : null;

  const selectedGroupRank = selectedPair
    ? ranks.byGroup.get(selectedPair.group_id)?.get(selectedPair.id)
    : null;

  const selectedVisibleIndex = selectedPair
    ? visiblePairs.findIndex((pair) => pair.id === selectedPair.id)
    : -1;

  const previousPair =
    selectedVisibleIndex > 0 ? visiblePairs[selectedVisibleIndex - 1] : null;

  const nextPair =
    selectedVisibleIndex >= 0 && selectedVisibleIndex < visiblePairs.length - 1
      ? visiblePairs[selectedVisibleIndex + 1]
      : null;

  const allReviewPairs = useMemo(
    () =>
      [...pairs]
        .filter(isReviewItem)
        .sort(
          (a, b) =>
            getDisplayQuality(a).score - getDisplayQuality(b).score ||
            a._bookIndex - b._bookIndex
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pairs]
  );

  const nextReviewPair = selectedPair
    ? allReviewPairs.find(
        (pair) =>
          pair._bookIndex > selectedPair._bookIndex &&
          pair.id !== selectedPair.id
      ) ?? allReviewPairs.find((pair) => pair.id !== selectedPair.id)
    : null;

  const qualityCounts = useMemo(() => {
    const counts = { green: 0, yellow: 0, red: 0 };
    const base = query.trim()
      ? pairs.filter((pair) => {
          if (viewMode === "review" && !isReviewItem(pair)) return false;
          const normalizedQuery = normalize(query).trim();
          if (!normalizedQuery) return true;
          const forms = getDisplayForms(pair);
          return normalize(
            [
              forms.accent1,
              forms.accent2,
              pair.id,
              pair.group_id,
              pair?.source?.raw_ocr,
            ].join(" ")
          ).includes(normalizedQuery);
        })
      : pairsByGroup.get(selectedGroupId) ?? [];

    base.forEach((pair) => {
      counts[getDisplayQuality(pair).level] += 1;
    });
    return counts;
  }, [pairs, pairsByGroup, query, selectedGroupId, viewMode]);

  const visibleCountsByGroup = useMemo(() => {
    const result = new Map();

    groups.forEach((group) => {
      const count = (pairsByGroup.get(group.id) ?? []).filter(matchesPair).length;
      result.set(group.id, count);
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, pairsByGroup, query, qualityFilter, viewMode]);

  const reviewCountsByGroup = useMemo(() => {
    const result = new Map();

    groups.forEach((group) => {
      const count = (pairsByGroup.get(group.id) ?? []).filter(isReviewItem).length;
      result.set(group.id, count);
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, pairsByGroup]);

  function applyReview(pairId, patch) {
    setReviewOverrides((current) => {
      const next = {
        ...current,
        [pairId]: {
          ...current[pairId],
          ...patch,
        },
      };

      onReviewChange?.({
        pairId,
        review: next[pairId],
        allReviews: next,
      });

      return next;
    });
  }

  function resolveScanImageUrl(pair) {
    if (!pair) return null;
    if (getScanImageUrl) return getScanImageUrl(pair);
    if (!scanImageBasePath || !pair?.source?.viewer_page) return null;

    return `${scanImageBasePath.replace(/\/$/, "")}/page_${padPage(
      pair.source.viewer_page
    )}.jpg`;
  }

  function resolveSourcePageUrl(pair) {
    if (!pair) return null;
    if (getSourcePageUrl) return getSourcePageUrl(pair);

    const itemId = data?.source?.nb_item_id;
    const page = pair?.source?.viewer_page;
    if (!itemId || !page) return null;

    return `https://www.nb.no/items/${itemId}?page=${page}`;
  }

  function toggleEndingClass(id) {
    setOpenEndingClasses((current) => ({
      ...current,
      [id]: !current[id],
    }));
  }

  function selectGroup(groupId) {
    setSelectedGroupId(groupId);
    setQuery("");
    setMobileGroupsOpen(false);
  }

  if (!data || groups.length === 0) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-950">
        <div className="font-semibold">No tonelag dataset loaded</div>
        <p className="mt-1 text-sm">
          Pass the enriched JSON object through the component&apos;s{" "}
          <code className="rounded bg-white/70 px-1">data</code> prop.
        </p>
      </div>
    );
  }

  return (
    <div
      data-tonelag-ui-version="research-v2"
      className="min-h-screen bg-[#f3f0e7] text-slate-900"
    >
      <header className="border-b border-stone-300 bg-[#faf8f2]">
        <div className="mx-auto max-w-[1800px] px-4 py-5 sm:px-6 lg:px-8">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">
            Martin Kloster Jensen · 1958
          </div>
          <h1 className="mt-1 font-serif text-3xl font-semibold tracking-tight text-slate-950">
            {title}
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Research database for comparing the OCR-derived inventory with the
            printed book. Two tonelag are organised into 21 grammatical and
            phonological pair-structure groups.
          </p>
          <QualityLegend />
        </div>
      </header>

      <div className="mx-auto grid max-w-[1800px] grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="border-b border-stone-300 bg-[#faf8f2] lg:min-h-[calc(100vh-150px)] lg:border-b-0 lg:border-r">
          <div className="sticky top-0 max-h-screen overflow-y-auto p-4 sm:p-5">
            <button
              type="button"
              onClick={() => setMobileGroupsOpen((value) => !value)}
              className="flex w-full items-center justify-between rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-semibold lg:hidden"
            >
              Browse 21 groups
              <ChevronIcon open={mobileGroupsOpen} />
            </button>

            <nav
              aria-label="Tonelag pair-structure groups"
              className={`${mobileGroupsOpen ? "block" : "hidden"} mt-3 space-y-3 lg:block`}
            >
              {endingClasses.map((endingClass) => {
                const open = openEndingClasses[endingClass.id] !== false;
                const classReviewCount = (endingClass.groups ?? []).reduce(
                  (sum, groupId) => sum + (reviewCountsByGroup.get(groupId) ?? 0),
                  0
                );

                return (
                  <section
                    key={endingClass.id}
                    className="rounded-lg border border-stone-200 bg-white/50"
                  >
                    <button
                      type="button"
                      onClick={() => toggleEndingClass(endingClass.id)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
                    >
                      <span>
                        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-600">
                          {ENDING_CLASS_LABELS[endingClass.id] ??
                            endingClass.label ??
                            endingClass.id}
                        </span>
                        <span className="ml-2 text-[11px] text-stone-400">
                          {endingClass.reported_share_percent}%
                        </span>
                      </span>

                      <span className="flex items-center gap-2">
                        {classReviewCount > 0 ? (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
                            {classReviewCount} review
                          </span>
                        ) : null}
                        <ChevronIcon open={open} />
                      </span>
                    </button>

                    {open ? (
                      <div className="space-y-1 border-t border-stone-200 p-2">
                        {(endingClass.groups ?? [])
                          .map((groupId) => groupsById.get(groupId))
                          .filter(Boolean)
                          .map((group) => (
                            <GroupNavButton
                              key={group.id}
                              group={group}
                              selected={!query.trim() && selectedGroupId === group.id}
                              visibleCount={visibleCountsByGroup.get(group.id)}
                              reviewCount={reviewCountsByGroup.get(group.id) ?? 0}
                              onSelect={selectGroup}
                            />
                          ))}
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </nav>
          </div>
        </aside>

        <main className="min-w-0 px-4 py-5 sm:px-6 lg:px-8">
          <div className="sticky top-0 z-20 -mx-4 -mt-5 border-b border-stone-300 bg-[#f3f0e7]/95 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
                <div className="relative min-w-0 flex-1 xl:max-w-xl">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">
                    <SearchIcon />
                  </span>
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search forms, IDs, OCR text or page numbers…"
                    className="w-full rounded-lg border border-stone-300 bg-white py-2 pl-9 pr-3 text-sm outline-none transition placeholder:text-stone-400 focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  />
                </div>

                <div className="inline-flex rounded-lg border border-stone-300 bg-white p-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("browse")}
                    className={[
                      "rounded-md px-3 py-1.5 text-xs font-semibold transition",
                      viewMode === "browse"
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:bg-stone-100",
                    ].join(" ")}
                  >
                    Browse
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setViewMode("review");
                      setQualityFilter("all");
                    }}
                    className={[
                      "rounded-md px-3 py-1.5 text-xs font-semibold transition",
                      viewMode === "review"
                        ? "bg-amber-400 text-amber-950"
                        : "text-slate-600 hover:bg-stone-100",
                    ].join(" ")}
                  >
                    Review queue · {allReviewPairs.length}
                  </button>
                </div>

                <select
                  value={sortMode}
                  onChange={(event) => setSortMode(event.target.value)}
                  disabled={viewMode === "review"}
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:bg-stone-100 disabled:text-stone-400"
                  aria-label="Sort pairs"
                >
                  <option value="book">Book order</option>
                  <option value="frequency">Surface frequency</option>
                  <option value="confidence">Text confidence</option>
                </select>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <FilterButton
                  active={qualityFilter === "all"}
                  onClick={() => setQualityFilter("all")}
                >
                  All
                </FilterButton>
                <FilterButton
                  tone="green"
                  active={qualityFilter === "green"}
                  onClick={() => setQualityFilter("green")}
                >
                  Green {qualityCounts.green}
                </FilterButton>
                <FilterButton
                  tone="yellow"
                  active={qualityFilter === "yellow"}
                  onClick={() => setQualityFilter("yellow")}
                >
                  Yellow {qualityCounts.yellow}
                </FilterButton>
                <FilterButton
                  tone="red"
                  active={qualityFilter === "red"}
                  onClick={() => setQualityFilter("red")}
                >
                  Red {qualityCounts.red}
                </FilterButton>
              </div>
            </div>
          </div>

          <section className="mt-6 overflow-hidden rounded-2xl border border-stone-300 bg-[#fbfaf6] shadow-sm">
            <div className="border-b border-stone-300 px-5 py-5 sm:px-7">
              {query.trim() ? (
                <>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                    Search across all 21 groups
                  </div>
                  <h2 className="mt-1 font-serif text-2xl font-semibold text-slate-950">
                    {visiblePairs.length} matching records
                  </h2>
                </>
              ) : (
                <>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                    {ENDING_CLASS_LABELS[selectedGroup?.ending_class] ??
                      selectedGroup?.ending_class}
                  </div>
                  <h2 className="mt-1 font-serif text-2xl font-semibold text-slate-950">
                    {selectedGroup?.id}. gruppe
                  </h2>
                  <p className="mt-1 font-serif text-base text-slate-700">
                    Eksempel: {selectedGroup?.example}
                  </p>
                  <p className="mt-2 max-w-4xl text-sm text-slate-600">
                    {selectedGroup?.description}
                  </p>

                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full border border-stone-300 bg-white px-2.5 py-1 text-slate-600">
                      Reported {nf.format(selectedGroup?.reported_count ?? 0)}
                    </span>
                    <span className="rounded-full border border-stone-300 bg-white px-2.5 py-1 text-slate-600">
                      Extracted{" "}
                      {nf.format(
                        selectedGroup?.extracted_pair_candidates ??
                          (pairsByGroup.get(selectedGroupId) ?? []).length
                      )}
                    </span>
                    <span className="rounded-full border border-stone-300 bg-white px-2.5 py-1 text-slate-600">
                      Share {selectedGroup?.reported_share_percent ?? "—"}%
                    </span>
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-900">
                      Review {reviewCountsByGroup.get(selectedGroupId) ?? 0}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="px-4 py-4 sm:px-6">
              {viewMode === "review" ? (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                  <span className="font-semibold">Review mode:</span> records are
                  ordered from lowest to highest text confidence. Open a record,
                  compare it with the scanned page, and mark it verified,
                  corrected or rejected.
                </div>
              ) : null}

              {visiblePairs.length === 0 ? (
                <div className="rounded-xl border border-dashed border-stone-300 px-6 py-14 text-center text-sm text-stone-500">
                  No records match the current search and filters.
                </div>
              ) : (
                <div className="columns-1 gap-x-5 sm:columns-2 xl:columns-3 2xl:columns-4">
                  {visiblePairs.map((pair) => (
                    <PairRow
                      key={pair.id}
                      pair={pair}
                      selected={selectedPairId === pair.id}
                      onSelect={(chosenPair) => setSelectedPairId(chosenPair.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>

          <div className="mt-4 grid gap-3 text-xs leading-5 text-stone-500 lg:grid-cols-2">
            <p>
              Text confidence is a transparent review heuristic. It is not a
              calibrated probability that every letter is correct.
            </p>
            <p>
              Corpus frequency concerns written forms. Identical spellings may
              combine several meanings, grammatical analyses and tonelag.
            </p>
          </div>
        </main>
      </div>

      <PairDetailsDrawer
        pair={selectedPair}
        group={
          selectedPair ? groupsById.get(selectedPair.group_id) : selectedGroup
        }
        globalRank={selectedGlobalRank}
        groupRank={selectedGroupRank}
        scanImageUrl={resolveScanImageUrl(selectedPair)}
        sourcePageUrl={resolveSourcePageUrl(selectedPair)}
        previousPair={previousPair}
        nextPair={nextPair}
        nextReviewPair={nextReviewPair}
        onNavigate={(pair) => {
          setSelectedPairId(pair.id);
          setSelectedGroupId(pair.group_id);
        }}
        onClose={() => setSelectedPairId(null)}
        onApplyReview={applyReview}
      />
    </div>
  );
}

export { DEFAULT_MANUAL_CORRECTIONS };
